
import { Prop, Component } from 'vue-property-decorator';
import type AnalysisActions from '@/logic/AnalysisActions';
import type {
  Label, LabelId, Unit, Pixel, Staff, Duration, Onset, Point,
} from '@/data';
import { StaffType } from '@/data';
import PixelUtils from '@/data/PixelUtils';
import type { Image3DPageData } from '@/data/Source';
import type { CanvasMouseEvent } from '../LabelBehavior.vue';
import LabelBehavior from '../LabelBehavior.vue';
import { roundX } from './utils';
import type { BEdgeDragEmitter } from '../scroll/BScrollOnEdgeDrag.vue';

interface State {
  target: LabelId;
  distToOnset: Point;
  isBar: boolean;
  duration: Duration;
}

/**
 * On drag click over a label, move the label
 *
 * Can move along a line and/or from line to line
 */
@Component
export default class BMoveOnDrag extends LabelBehavior<BEdgeDragEmitter> {
  @Prop()
  actions!: AnalysisActions;

  @Prop()
  pixelUnit!: Unit<Pixel>;

  @Prop()
  staves!: Staff[];

  @Prop({ default: false })
  snap!: boolean;

  @Prop({ default: false })
  snappableWave!: boolean;

  @Prop({ required: true })
  pages!: Array<Image3DPageData>;

  state: State | null = null;

  startDrag(event: CanvasMouseEvent, label: Label): void {
    const { duration, id: target } = label;

    const labelX = this.pixelUnit.fromOnset(label.onset);

    const distToOnset = {
      x: (PixelUtils.deMux(labelX.value).x - event.x) as Pixel,
      y: (PixelUtils.getYAsPixel(this.pages, labelX.value) - event.y) as Pixel,
    };
    const isBar = duration === 0 && label.staffId === 0;

    this.state = {
      target, distToOnset, isBar, duration,
    };

    this.$emit('drag', true);
  }

  dragHorizontal(event: CanvasMouseEvent): void {
    if (this.state === null) return;

    let x = PixelUtils
      .getMuxPixelFromPosition(
        this.pages,
        (event.x + this.state.distToOnset.x) as Pixel,
        (event.y) as Pixel,
      );

    // Get the onset
    if (this.snappableWave && this.pixelUnit.onsetsCoverage() <= 0.8) {
      x = roundX(x);
    }
    let onset = this.pixelUnit.toOnset(x, this.snap).value;
    if (this.snappableWave && this.pixelUnit.onsetsCoverage() > 0.8) {
      onset = Math.round(onset) as Onset;
    }
    let duration: Duration;

    // If the label is being dragged offscreen to the left
    if (this.pixelUnit.isOutOfBounds(x) < 0) {
      // Get the current pixel size of the label
      const labelSize = this.pixelUnit.fromInterval(onset, this.state.duration);
      // Get the pixel distance to the label offset
      const distToOffset = (this.state.distToOnset.x + labelSize.width) as Pixel;
      // Get the pixel position of the new duration
      const offsetX = (event.x + distToOffset) as Pixel;

      // Calculate the new duration
      // By using the new position as an onset
      duration = this.pixelUnit.toInterval(0 as Pixel, offsetX).duration;
    } else {
      duration = this.state.duration;
    }

    const interval = this.pixelUnit.boundInterval(onset, duration, this.snap);

    this.actions.updateLabel(this.state.target, 'onset', interval.onset);
    this.actions.updateLabel(this.state.target, 'duration', interval.duration);
    // Scroll if x is on canvas edge
    this.emit('check-edge-drag', event.x);
  }

  dragVertical(_: CanvasMouseEvent, staff: Staff): void {
    if (this.state === null) return;
    // It is not possible to move to or from the bar staff by dragging
    // If the label is of type bar, prevent changing the staff
    if (this.state.isBar) return;
    // If for some reason the destination is the bar staff, return
    // This should not be possible, as the bar staff doesn't receive mouse events
    if (staff.type === StaffType.bar) return;

    this.actions.updateLabel(this.state.target, 'staffId', staff.id);
  }

  endDrag() {
    this.state = null;
    this.$emit('drag', false);
  }

  mounted() {
    this.register('label-mousedown', this.startDrag);
    this.register('all-mousemove', this.dragHorizontal);
    this.register('staff-mousemove', this.dragVertical);
    this.register('label-mousemove', (e, label, staff) => this.dragVertical(e, staff));
    this.register('document-mouseup', this.endDrag);
  }
}
